import { useEffect } from 'react';
import { useUploadStore } from '../../../../store/uploadStore';
import { setupUpdateSpeedInterval } from './setupUpdateSpeedInterval';

const MAX_MEASUREMENTS = 300;
const MEASUREMENT_INTERVAL_MS = 1000;
const MIN_MEASUREMENTS_FOR_AVERAGE = 20;

let averageBytesPerSecond: number | null = null;
let previousSize = 0;

export const getAverageBytesPerSecond = () => averageBytesPerSecond ?? 0;

const getTotalSize = () => {
  const uploads = useUploadStore.getState().uploads;
  return Object.values(uploads).reduce((acc, upload) => acc + (upload.meta.uploadedSize ?? 0), 0);
};

/**
 * When restoring old uploads, we distort the measurements and need to reset them
 */
export const resetUploadSpeedMeasurement = () => {
  previousSize = getTotalSize();
};

/**
 * This hook must be called only once
 */
export const useMeasureUploadSpeed = () => {
  useEffect(() => {
    let hasInitialMeasurements = false;
    let previousTimestamp = Date.now();
    const speeds: number[] = [];
    let timeoutId: NodeJS.Timeout;

    const measureSpeed = () => {
      const currentTimestamp = Date.now();
      const elapsedSeconds = (currentTimestamp - previousTimestamp) / 1000;

      if (elapsedSeconds <= 0) {
        scheduleNext();
        return;
      }

      previousTimestamp = currentTimestamp;

      const totalSize = getTotalSize();

      if (totalSize <= previousSize) {
        previousSize = totalSize;
        scheduleNext();
        return;
      }

      const bytesTransferred = totalSize - previousSize;
      previousSize = totalSize;

      const speed = bytesTransferred / elapsedSeconds;

      speeds.push(speed);

      if (speeds.length > MAX_MEASUREMENTS) {
        speeds.shift();
      }

      if (speeds.length >= MIN_MEASUREMENTS_FOR_AVERAGE) {
        averageBytesPerSecond = speeds.reduce((acc, val) => acc + val, 0) / speeds.length;

        if (!hasInitialMeasurements) {
          useUploadStore.getState().setBytesPerSecond(averageBytesPerSecond);
          setupUpdateSpeedInterval();
          hasInitialMeasurements = true;
        }
      }

      scheduleNext();
    };

    const scheduleNext = () => {
      timeoutId = setTimeout(measureSpeed, MEASUREMENT_INTERVAL_MS);
    };

    scheduleNext();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);
};
